<template>
  <loading-placeholder v-if="isLoading && !hasCachedMemberData" :lines="1" class="mailbox-home-header-options-loading" />
  <div v-else class="mailbox-home-header-options">
    <div class="mailbox-home-header-options__options">
      <!-- Export -->
      <item-export-picker
        v-if="$permissions.isAdminOrOwner"
        :entity-type="EEntities.MAILBOXES"
        :entity-id="mailboxId"
        :total-item-count="totalItemCount"
        class="main-icon" 
      />
      <!-- Notifications -->
      <email-notifications-picker 
        :entity-type="EEntities.MAILBOXES"
        :entity-id="mailboxId"
        class="main-icon" 
      />
      <!-- Calendar -->
      <el-tooltip placement="top">
        <template #content>
          <div>Go to Calendar</div>
        </template>
        <router-link :to="RouteAssistant.getCalendarRoute(accountId, mailboxId)" title="Calendar">
          <i class="main-icon ddi ddi-calendar" />
        </router-link>
      </el-tooltip>
      <!-- Reports -->
      <el-tooltip placement="top">
        <template #content>
          <div>Go to Reports</div>
        </template>
        <a :href="RouteAssistant.getReportsUrl(accountId, mailboxId)" title="Reports">
          <i class="main-icon ddi ddi-reports" />
        </a>
      </el-tooltip>
      <!-- Settings -->
      <more-button-dropdown v-if="$permissions.isAdminOrOwner" title="Settings">
        <template #options-list>
          <template v-for="menu in menuList" :key="menu.label">
            <h5>{{menu.label}}</h5>
            <ul>
              <li
                v-for="option in menu.options"
                :key="option.value"
                @click="sendToSettingsLink(option.value)"
              >
                <i :class="'ddi-'+option.icon" class="ddi" />
                <span>{{option.label}}</span>
              </li>
            </ul>
          </template>
        </template>
      </more-button-dropdown>
      <span class="vertical-divider" />
    </div>
    <!-- cachedMemberData.members will always appear here because: 
      (1) It immediately shows (because it was already cached)
      (2) It is updated after members prop is updated (which updates cache via members watch)
          Thus, no need to show loading...allow the data to be 'stale' momentarily until parent
          provides the prop.
      (3) Otherwise, isLoading invoked above until members prop is hydrated
    -->
    <avatar-gallery
      :people="cachedMemberData ? cachedMemberData.members : members"
      :max-num-avatars="5"
      stack-tightly
    />
    <quick-add-member-picker
      v-if="$permissions.isAdminOrOwner"
      :entity-type="EEntities.MAILBOXES"
      :entity-id="mailboxId"
      class="mailbox-home-header-options__add-people-button"
      @members-added="$emit('members-added')"
    />
    <div class="mailbox-home-header-options__new-convo-button">
      <new-conversation-button :mailbox-id="mailboxId" :mailbox-name="mailboxName" size="medium" />
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import * as routerNames from '@/services/router/router.names'
import {
  ContentPlaceholders,
  ContentPlaceholdersText,
} from '@/components/global/content-placeholders'

/* Mixins */

/* Components */

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import { IPersonBase } from '@/api/types/response.types'
import { EEntities } from '@/types'
import {
  IMemberDataFromEntityLanding,
  INavMenuGroup,
} from '@/types/data-objects/global'
import { useAppStore } from '@/modules/app/app.store'

/* API */

export default defineComponent({
  name: 'MailboxHomeHeaderOptions',

  components: {
    ContentPlaceholders,
    ContentPlaceholdersText,
  },

  props: {
    members: {
      type: Array as () => IPersonBase[],
      required: true,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    accountId: {
      type: Number,
      required: true,
    },

    mailboxId: {
      type: Number,
      required: true,
    },

    mailboxName: {
      type: String,
      default: '',
    },

    isDirectConnection: {
      type: Boolean,
      default: true,
    },

    totalItemCount: {
      type: Number,
      default: null,
    },
  },

  emits: ['members-added'],

  data() {
    return {
      EEntities,
      RouteAssistant,
    }
  },

  computed: {
    cachedMemberData(): IMemberDataFromEntityLanding {
      return useAppStore().lastVisitedEntityLandingMemberData
    },

    hasCachedMemberData(): boolean {
      return (
        this.cachedMemberData &&
        this.cachedMemberData.entityID === this.mailboxId
      )
    },

    menuList(): INavMenuGroup[] {
      const items = [
        {
          label: 'Setup',
          options: [
            {
              value: routerNames.MAILBOX_SETTINGS_DIRECT_CONNECTION,
              label: 'Direct Connection',
              icon: 'link',
            },
            ...(this.isDirectConnection
              ? []
              : [
                  {
                    value: routerNames.MAILBOX_SETTINGS_AUTO_FORWARDING,
                    label: 'Auto-Forwarding',
                    icon: 'auto-forwarding',
                  },
                  {
                    value: routerNames.MAILBOX_SETTINGS_OUTGOING_EMAIL,
                    label: 'Outgoing Email',
                    icon: 'outgoing-email',
                  },
                ]),
          ],
        },
        {
          label: 'Manage',
          options: [
            {
              value: routerNames.MAILBOX_SETTINGS_ASSIGN_PEOPLE,
              label: 'Add/Remove Users',
              icon: 'people',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CONVERSATION_RULES,
              label: 'Conversation Rules',
              icon: 'incoming-email',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CHECKLIST_TEMPLATES,
              label: 'Checklist Templates',
              icon: 'everything',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_CUSTOM_FIELDS,
              label: 'Custom Fields',
              icon: 'three-blocks',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_SAVED_REPLIES,
              label: 'Saved Replies',
              icon: 'saved-replies',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_OFFICE_HOURS,
              label: 'Office Hours',
              icon: 'clock',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_AUTO_RESPONDER,
              label: 'Auto-Responder',
              icon: 'auto-responder',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_EMAIL_SIGNATURE,
              label: 'Email Signature',
              icon: 'edit',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_MANAGE_WORKFLOW,
              label: 'Manage Workflow',
              icon: 'workflows',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_WORKFLOW_AUTOMATIONS,
              label: 'Workflow Automations',
              icon: 'workflows',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_GENERAL_SETTINGS,
              label: 'General Settings',
              icon: 'cog',
            },
            {
              value: routerNames.MAILBOX_SETTINGS_INTEGRATIONS,
              label: 'Integrations',
              icon: 'integrations',
            },
          ],
        },
      ]

      /* Eventually, always add this option to menu and let AO click because the ticket portal page 
         itself will have the <feature-gated-stamp /> applied to it. */
      if (this.$permissions.ticketPortalFeatureEnabled) {
        items.find(i => i.label === 'Setup').options.push({
          value: routerNames.MAILBOX_SETTINGS_TICKET_PORTAL,
          label: 'Ticket Portal',
          icon: 'ticket-portal',
        })
      }
      return items.filter(i => i.options.length)
    },
  },

  watch: {
    members: {
      deep: 1,
      handler() {
        this.cacheMemberDataFromEntityLanding()
      },
    },
  },

  methods: {
    cacheMemberDataFromEntityLanding() {
      const data = {
        entityID: this.mailboxId,
        members: this.members,
      } as IMemberDataFromEntityLanding

      useAppStore().lastVisitedEntityLandingMemberData = data
    },
    sendToSettingsLink(value: string) {
      this.$router.push({
        name: value,
        params: {
          accountId: this.$route.params.accountId,
          id: this.$route.params.id,
        },
      })
    },
  },
})
</script>

<style lang="scss">
.mailbox-home-header-options {
  display: flex;
  align-items: center;

  &-loading {
    position: relative;
    // Makes it look good on mobile too (with 10% side padding on vertical stacked layout)
    width: 90%;
    height: 32px;

    .vue-content-placeholders-text__line {
      position: absolute;
      width: 100% !important;
      height: 100%;
    }
  }

  &__options {
    display: flex;
    align-items: center;

    .main-icon {
      font-size: 21px;
      padding: 0 8px;
      @include clickable-gray-color-states;
    }
  }

  .vertical-divider {
    width: 1px;
    height: 24px;
    margin: 0 12px;
    background-color: $color--gray-30;
  }

  &__add-people-button {
    margin-left: 6px;
  }

  &__new-convo-button {
    margin-left: 24px;
  }
}
</style>
